/* ==========================================================================
   ZARGES USF GRID ENHANCER
   --------------------------------------------------------------------------
   Self-contained script that adds two enhancements to product cards
   rendered by the AI Search & Product Filter app (USF/Sparq):

   1. Hover spec overlay with Quick View button (Zarges-branded)
   2. Secondary image swap on hover (cross-fades to product image #2)

   Approach: instead of extending USF's Vue components (which would require
   deep app knowledge and risks breaking things), this script waits for USF
   to render its cards, then enhances each card from outside.

   Hooks into the `sr_updated` event that USF fires after every render.
   ========================================================================== */

(function() {
  'use strict';

  // ---- Wait for USF to be ready ------------------------------------------
  function waitForUsf(callback) {
    var attempts = 0;
    var interval = setInterval(function() {
      attempts++;
      if (window.usf && window.usf.event && typeof window.usf.event.add === 'function') {
        clearInterval(interval);
        callback();
      } else if (attempts > 100) {
        clearInterval(interval);
        console.warn('[Zarges] USF never became available; enhancement not applied.');
      }
    }, 100);
  }

  // ---- Read a metafield value off a USF product object -------------------
  function getMeta(product, namespace, key) {
    if (!product || !product.metafields) return '';
    var mf = product.metafields.find(function(m) {
      return m.namespace === namespace && m.key === key;
    });
    if (!mf || !mf.value) return '';
    var raw = mf.value;
    try {
      var parsed = JSON.parse(raw);
      return parsed.value || parsed;
    } catch (e) {
      return raw;
    }
  }

  // ---- Build the spec object for a product -------------------------------
  function buildSpecs(product) {
    var ext_l = getMeta(product, 'my_fields', 'external_length');
    var ext_w = getMeta(product, 'my_fields', 'external_width');
    var ext_h = getMeta(product, 'my_fields', 'external_height');
    var int_l = getMeta(product, 'my_fields', 'internal_length');
    var int_w = getMeta(product, 'my_fields', 'internal_width');
    var int_h = getMeta(product, 'my_fields', 'internal_height');
    var volume = getMeta(product, 'my_fields', 'volume');
    var weight = getMeta(product, 'custom', 'weight');

    var exterior = (ext_l && ext_w && ext_h) ? (ext_l + 'in × ' + ext_w + 'in × ' + ext_h + 'in') : '';
    var interior = (int_l && int_w && int_h) ? (int_l + 'in × ' + int_w + 'in × ' + int_h + 'in') : '';

    return {
      exterior: exterior,
      interior: interior,
      volume: volume,
      weight: weight,
      hasAny: !!(exterior || interior || volume || weight)
    };
  }

  // ---- Spec icon SVGs ----------------------------------------------------
  var ICONS = {
    exterior: '<svg class="zarges-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="1"/><line x1="3" y1="9" x2="21" y2="9"/></svg>',
    interior: '<svg class="zarges-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="1"/><rect x="6" y="8" width="12" height="8" rx="0.5" stroke-dasharray="2 2"/></svg>',
    volume: '<svg class="zarges-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3h14l-1.5 17a1 1 0 0 1-1 .9H7.5a1 1 0 0 1-1-.9L5 3z"/><path d="M6 11h12" stroke-dasharray="1 2"/></svg>',
    weight: '<svg class="zarges-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8h14l-1.5 12h-11L5 8z"/><circle cx="12" cy="5" r="2"/><path d="M10 5h4"/></svg>'
  };

  // ---- Build the overlay HTML for a product ------------------------------
  function buildOverlayHtml(product, specs) {
    var rows = '';
    if (specs.exterior) {
      rows += '<div class="zarges-spec">' + ICONS.exterior +
              '<div class="zarges-spec-text"><div class="zarges-spec-value">' + specs.exterior + '</div><div class="zarges-spec-label">Exterior</div></div></div>';
    }
    if (specs.interior) {
      rows += '<div class="zarges-spec">' + ICONS.interior +
              '<div class="zarges-spec-text"><div class="zarges-spec-value">' + specs.interior + '</div><div class="zarges-spec-label">Interior</div></div></div>';
    }
    if (specs.volume) {
      rows += '<div class="zarges-spec">' + ICONS.volume +
              '<div class="zarges-spec-text"><div class="zarges-spec-value">' + specs.volume + '</div><div class="zarges-spec-label">Volume</div></div></div>';
    }
    if (specs.weight) {
      rows += '<div class="zarges-spec">' + ICONS.weight +
              '<div class="zarges-spec-text"><div class="zarges-spec-value">' + specs.weight + '</div><div class="zarges-spec-label">Weight</div></div></div>';
    }

    var titleSafe = (product.title || '').replace(/"/g, '&quot;');
    var url = '/products/' + (product.urlName || '');

    // Pull the variant ID and price the same way USF would
    var variant = (product.variants && product.variants.length) ? product.variants[0] : null;
    var variantId = variant ? variant.id : '';
    var price = variant ? formatPrice(variant.price) : '';
    var imageUrl = (product.images && product.images.length) ? getImageUrl(product.images[0], '1024') : '';

    // Note: availability and call-to-order tag are looked up at modal-open
    // time by fetching /products/{handle}.js (Shopify's canonical source of
    // truth). USF's product data shape varies and isn't reliable for this.

    return '<div class="zarges-spec-overlay" aria-hidden="true">' +
              '<div class="zarges-spec-grid">' + rows + '</div>' +
              '<button type="button" class="zarges-quickview-btn" ' +
                'data-zarges-quickview ' +
                'data-product-title="' + titleSafe + '" ' +
                'data-product-handle="' + (product.urlName || '') + '" ' +
                'data-product-url="' + url + '" ' +
                'data-product-image="' + imageUrl + '" ' +
                'data-product-price="' + price + '" ' +
                'data-product-variant-id="' + variantId + '" ' +
                'data-exterior="' + specs.exterior + '" ' +
                'data-interior="' + specs.interior + '" ' +
                'data-volume="' + specs.volume + '" ' +
                'data-weight="' + specs.weight + '" ' +
                'aria-label="Quick view for ' + titleSafe + '">' +
                'Quick View ' +
                '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 7h12M7 1l6 6-6 6"/></svg>' +
              '</button>' +
            '</div>';
  }

  // ---- Find a product by ID in the latest USF result ---------------------
  function findProductById(productId) {
    if (!window.usf || !window.usf.search || !window.usf.search.result) return null;
    var items = window.usf.search.result.items || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].id) === String(productId)) return items[i];
    }
    return null;
  }

  // ---- Get the URL for an image at a given size --------------------------
  function getImageUrl(image, size) {
    if (!image || !image.url) return '';
    var url = image.url;

    // Normalize protocol-relative URLs (USF often returns these)
    if (url.indexOf('//') === 0) {
      url = 'https:' + url;
    }

    // For non-Shopify URLs or theme assets, return as-is
    if (url.indexOf('shopify.com') === -1 && url.indexOf('shopifycdn') === -1) {
      return url;
    }
    if (url.indexOf('/assets/') !== -1) return url;

    // Use Shopify's query parameter approach (works on all Shopify CDN URLs)
    // This is much safer than rewriting the path
    if (size) {
      var separator = url.indexOf('?') === -1 ? '?' : '&';
      // Strip any existing width/height params first
      url = url.replace(/[?&](width|height)=\d+/g, '');
      separator = url.indexOf('?') === -1 ? '?' : '&';
      url = url + separator + 'width=' + size;
    }

    return url;
  }

  // ---- Format a price using USF's currency converter if available --------
  function formatPrice(price) {
    if (price === undefined || price === null) return '';
    try {
      if (window.usf && window.usf.currencyConverter && window.usf.currencyConverter.format) {
        return window.usf.currencyConverter.format(price);
      }
    } catch (e) {}
    // Fallback: simple USD format
    return '$' + (price / 1).toFixed(2);
  }

  // ---- Wire up the secondary image hover on a card ----------------------
  function setupSecondaryImage(card, product) {
    // Skip if we've already set it up
    if (card.dataset.zargesSecondaryReady === '1') return;

    // Skip if the theme already rendered its own secondary image element
    // (Concept theme uses .product-card__secondary). The CSS handles
    // making it cover-fill; no JS injection needed for those cards.
    if (card.querySelector('.product-card__secondary')) {
      card.dataset.zargesSecondaryReady = '1';
      return;
    }

    // Need at least 2 images for a swap
    if (!product.images || product.images.length < 2) return;

    // Don't activate on touch devices (no hover)
    if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;

    // Find the primary image element rendered by USF
    var mediaWrap = card.querySelector('.product-card__media');
    if (!mediaWrap) return;

    var primaryImg = mediaWrap.querySelector('a img, .media img');
    if (!primaryImg) return;

    // Match the theme's native animation timing
    var THEME_TRANSITION = '0.5s cubic-bezier(0.3, 1, 0.3, 1)';

    // Add transitions for both opacity AND transform to the primary image.
    // (Without the opacity transition, the primary stayed visible underneath
    // the secondary on hover — the "two stacked images" bug.)
    primaryImg.style.transition = 'opacity ' + THEME_TRANSITION + ', transform ' + THEME_TRANSITION;
    primaryImg.style.transformOrigin = 'center center';

    // Leave the primary image alone — it should keep whatever fit mode the
    // theme set (typically `contain` for product shots, so the full product
    // is visible without cropping).

    // For the secondary (lifestyle) image: use `cover` so it fills the tile
    // edge-to-edge with no whitespace. The overlay is semi-transparent
    // (see zarges-quickview.css) so the subject shows through behind the
    // specs — center positioning works for most photography.
    var fitMode = 'cover';
    var fitPosition = 'center center';

    var secondaryImage = product.images[1];
    var secondaryImg = document.createElement('img');
    secondaryImg.className = 'zarges-secondary-img';
    secondaryImg.src = getImageUrl(secondaryImage, '720');
    secondaryImg.setAttribute('alt', secondaryImage.alt || '');
    secondaryImg.setAttribute('loading', 'lazy');
    // !important on size/position so theme CSS can't override us. We append
    // directly into .product-card__media (which we know fills the tile)
    // rather than the inner <a>, since that anchor may not have explicit
    // dimensions on every card layout.
    secondaryImg.style.cssText = [
      'position: absolute !important',
      'top: 0 !important',
      'left: 0 !important',
      'right: 0 !important',
      'bottom: 0 !important',
      'width: 100% !important',
      'height: 100% !important',
      'max-width: none !important',
      'max-height: none !important',
      'object-fit: ' + fitMode + ' !important',
      'object-position: ' + fitPosition,
      'opacity: 0',
      'visibility: hidden',
      'transform: scale(1)',
      'transform-origin: center center',
      'transition: opacity ' + THEME_TRANSITION + ', visibility ' + THEME_TRANSITION + ', transform ' + THEME_TRANSITION,
      'pointer-events: none',
      'z-index: 1',
      'margin: 0',
      'padding: 0'
    ].join('; ');

    // Make sure the media wrapper is positioned so we can absolutely-position
    // the secondary image relative to it. (mediaWrap is the full tile area.)
    var mediaPosition = window.getComputedStyle(mediaWrap).position;
    if (mediaPosition === 'static') {
      mediaWrap.style.position = 'relative';
    }
    mediaWrap.style.overflow = 'hidden';

    // Append directly to the media wrapper, NOT to the inner <a>.
    mediaWrap.appendChild(secondaryImg);

    // Hover handlers — crossfade primary OUT while secondary fades IN.
    // Primary still gets the 1.05x zoom (matches theme's native behavior).
    // Secondary stays at scale 1.0 — no zoom — just the parallax pan.
    var onEnter = function() {
      primaryImg.style.opacity = '0';
      primaryImg.style.transform = 'scale(1.05)';
      secondaryImg.style.opacity = '1';
      secondaryImg.style.visibility = 'visible';
      secondaryImg.style.transform = 'scale(1)';
    };
    var onLeave = function() {
      primaryImg.style.opacity = '1';
      primaryImg.style.transform = 'scale(1)';
      secondaryImg.style.opacity = '0';
      secondaryImg.style.visibility = 'hidden';
      secondaryImg.style.transform = 'scale(1)';
      // Reset object-position back to center when leaving
      secondaryImg.style.objectPosition = fitPosition;
    };

    // Parallax pan: as the mouse moves across the tile, shift the secondary
    // image's object-position to "look around" the lifestyle scene. We use
    // object-position (not transform) so the image still fills the tile
    // edge-to-edge — no whitespace gaps appear at the edges as we pan.
    //
    // Adaptive pan range: when `cover` crops a wide image, the hidden area
    // is on the LEFT and RIGHT — so wider images deserve more horizontal
    // pan. When `cover` crops a tall image, the hidden area is on the TOP
    // and BOTTOM — so taller images deserve more vertical pan.
    //
    // Adaptive pan range: with no zoom, the only "extra" image area
    // available to pan into is whatever `cover` is cropping off the sides
    // (for wide images) or top/bottom (for tall images). Near-square images
    // have no slack at all → no pan. Wide/tall images pan more.
    var BASE_PAN = 0;     // near-square images don't pan
    var MAX_PAN = 18;     // cap for very wide or very tall images
    var panX = BASE_PAN;
    var panY = BASE_PAN;

    function recalcPanRange() {
      var iw = secondaryImg.naturalWidth;
      var ih = secondaryImg.naturalHeight;
      var rect = mediaWrap.getBoundingClientRect();
      if (!iw || !ih || !rect.width || !rect.height) return;

      var imgAspect = iw / ih;
      var tileAspect = rect.width / rect.height;

      if (imgAspect > tileAspect) {
        // Image is wider than tile → horizontal slack > vertical slack
        var ratio = imgAspect / tileAspect;
        panX = Math.min(MAX_PAN, BASE_PAN + (ratio - 1) * 35);
        panY = BASE_PAN;
      } else if (imgAspect < tileAspect) {
        // Image is taller than tile → vertical slack > horizontal slack
        var ratio2 = tileAspect / imgAspect;
        panY = Math.min(MAX_PAN, BASE_PAN + (ratio2 - 1) * 35);
        panX = BASE_PAN;
      } else {
        panX = BASE_PAN;
        panY = BASE_PAN;
      }
    }

    if (secondaryImg.complete) {
      recalcPanRange();
    } else {
      secondaryImg.addEventListener('load', recalcPanRange);
    }
    // Recalc if the tile resizes (responsive layout changes)
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(recalcPanRange);
      ro.observe(mediaWrap);
    }

    var onMove = function(e) {
      var rect = mediaWrap.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      // Normalize cursor position to -1..1 range
      var nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      var ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      // Invert so the image moves OPPOSITE the cursor (look-around feel)
      var px = 50 - nx * panX;
      var py = 50 - ny * panY;
      secondaryImg.style.objectPosition = px.toFixed(2) + '% ' + py.toFixed(2) + '%';
    };

    // Add a short transition on object-position so cursor jitters smooth out
    secondaryImg.style.transition += ', object-position 0.15s ease-out';

    card.addEventListener('mouseenter', onEnter);
    card.addEventListener('mouseleave', onLeave);
    card.addEventListener('mousemove', onMove);

    card.dataset.zargesSecondaryReady = '1';
  }

  // ---- Mobile swipe carousel: primary + secondary in a scroll-snap track -
  // On touch devices there's no hover, so we turn the .product-card__media
  // area into a horizontal swipe carousel showing primary and secondary
  // images side-by-side. Native scroll-snap, no library, no JS animation.
  function setupMobileCarousel() {
    // Only on touch / small screens
    var isMobile = (window.matchMedia && window.matchMedia('(hover: none)').matches) ||
                   window.innerWidth <= 768;
    if (!isMobile) return;

    var mediaWrappers = document.querySelectorAll('.product-card__media');
    mediaWrappers.forEach(function(media) {
      if (media.dataset.zargesCarouselReady === '1') return;

      var anchor = media.querySelector('a');
      if (!anchor) return;

      // Find the primary image — the direct <img> child of the anchor
      // (avoid :scope for older Safari compat). We iterate children to
      // find the first <img> that ISN'T inside .product-card__secondary.
      var primaryImg = null;
      var secondaryWrap = null;
      for (var i = 0; i < anchor.children.length; i++) {
        var child = anchor.children[i];
        if (child.tagName === 'IMG' && !primaryImg) {
          primaryImg = child;
        } else if (child.classList && child.classList.contains('product-card__secondary')) {
          secondaryWrap = child;
        }
      }
      if (!primaryImg) return;

      var secondaryImg = secondaryWrap ? secondaryWrap.querySelector('img') : null;

      // Sanity check: secondary src must be different from primary, otherwise
      // we'd render the same image twice. Skip if they match (or if missing).
      var hasSecondary = false;
      if (secondaryImg && secondaryImg.src && secondaryImg.src !== primaryImg.src) {
        hasSecondary = true;
      }

      // Build carousel structure
      var carousel = document.createElement('div');
      carousel.className = 'zarges-mobile-carousel';

      // Slide 1: primary
      var slide1 = document.createElement('div');
      slide1.className = 'zarges-mobile-carousel__slide';
      var img1 = primaryImg.cloneNode(true);
      img1.removeAttribute('loading');
      slide1.appendChild(img1);
      carousel.appendChild(slide1);

      // Slide 2: secondary (only if we actually have a different image)
      if (hasSecondary) {
        var slide2 = document.createElement('div');
        slide2.className = 'zarges-mobile-carousel__slide';
        var img2 = secondaryImg.cloneNode(true);
        img2.removeAttribute('loading');
        slide2.appendChild(img2);
        carousel.appendChild(slide2);
      }

      // Tag the anchor so CSS can hide its original images
      anchor.classList.add('zarges-mobile-carousel-host');

      // Insert carousel at the start of the anchor's contents
      anchor.insertBefore(carousel, anchor.firstChild);

      // Dot indicators (only if 2 slides)
      if (hasSecondary) {
        var dots = document.createElement('div');
        dots.className = 'zarges-mobile-dots';
        dots.innerHTML = '<span class="zarges-mobile-dots__dot is-active"></span><span class="zarges-mobile-dots__dot"></span>';
        media.insertBefore(dots, anchor.nextSibling);

        var dotEls = dots.querySelectorAll('.zarges-mobile-dots__dot');
        var scrollTimer = null;
        carousel.addEventListener('scroll', function() {
          if (scrollTimer) clearTimeout(scrollTimer);
          scrollTimer = setTimeout(function() {
            var idx = Math.round(carousel.scrollLeft / carousel.clientWidth);
            dotEls.forEach(function(d, i) {
              d.classList.toggle('is-active', i === idx);
            });
          }, 50);
        });
      }

      // Suppress click navigation on swipe
      var touchStartX = 0;
      var touchStartY = 0;
      var swipeDetected = false;
      carousel.addEventListener('touchstart', function(e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        swipeDetected = false;
      }, { passive: true });
      carousel.addEventListener('touchmove', function(e) {
        var dx = Math.abs(e.touches[0].clientX - touchStartX);
        var dy = Math.abs(e.touches[0].clientY - touchStartY);
        if (dx > 10 && dx > dy) swipeDetected = true;
      }, { passive: true });
      anchor.addEventListener('click', function(e) {
        if (swipeDetected) {
          e.preventDefault();
          e.stopPropagation();
          swipeDetected = false;
        }
      });

      media.dataset.zargesCarouselReady = '1';
    });
  }

  // Some products (e.g. KOR Kit line) use a <video> as their secondary
  // media. The theme leaves it static and letterboxed; we make it fill
  // the tile (CSS) and autoplay muted on hover (JS). Pause on leave.
  function setupVideoHover() {
    var videos = document.querySelectorAll('.product-card__media video');
    videos.forEach(function(video) {
      if (video.dataset.zargesVideoReady === '1') return;

      // Required for browsers to allow unattended playback
      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.loop = true;
      // Don't preload aggressively — wait for hover
      if (!video.getAttribute('preload')) video.preload = 'metadata';

      var card = video.closest('.product-card') || video.closest('[data-usf-pid]') || video.parentElement;
      if (!card) return;

      var onEnter = function() {
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function() { /* swallow autoplay rejection */ });
      };
      var onLeave = function() {
        try { video.pause(); video.currentTime = 0; } catch (e) {}
      };

      card.addEventListener('mouseenter', onEnter);
      card.addEventListener('mouseleave', onLeave);

      video.dataset.zargesVideoReady = '1';
    });
  }

  // ---- Parallax pan on theme-rendered secondary images ------------------
  // The Concept theme renders its own .product-card__secondary on every
  // product card (Featured Collection AND USF). Since we don't inject our
  // own image when one already exists, we attach the look-around parallax
  // directly to the theme's element. Adapts pan range per axis based on
  // image aspect vs tile aspect — wide images pan more horizontally, tall
  // ones pan more vertically, near-square ones don't pan at all.
  function setupThemeSecondaryParallax() {
    var wrappers = document.querySelectorAll('.product-card__secondary');
    wrappers.forEach(function(wrapper) {
      if (wrapper.dataset.zargesParallaxReady === '1') return;

      var img = wrapper.querySelector('img');
      if (!img) return;

      var card = wrapper.closest('.product-card') || wrapper.closest('.card') || wrapper.parentElement;
      if (!card) return;

      // Don't activate on touch devices
      if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;

      var BASE_PAN = 0;
      var MAX_PAN = 18;
      var panX = BASE_PAN;
      var panY = BASE_PAN;

      function recalcPanRange() {
        var iw = img.naturalWidth;
        var ih = img.naturalHeight;
        var rect = wrapper.getBoundingClientRect();
        if (!iw || !ih || !rect.width || !rect.height) return;

        var imgAspect = iw / ih;
        var tileAspect = rect.width / rect.height;

        if (imgAspect > tileAspect) {
          var ratio = imgAspect / tileAspect;
          panX = Math.min(MAX_PAN, BASE_PAN + (ratio - 1) * 35);
          panY = BASE_PAN;
        } else if (imgAspect < tileAspect) {
          var ratio2 = tileAspect / imgAspect;
          panY = Math.min(MAX_PAN, BASE_PAN + (ratio2 - 1) * 35);
          panX = BASE_PAN;
        } else {
          panX = BASE_PAN;
          panY = BASE_PAN;
        }
      }

      if (img.complete) {
        recalcPanRange();
      } else {
        img.addEventListener('load', recalcPanRange);
      }
      if (window.ResizeObserver) {
        var ro = new ResizeObserver(recalcPanRange);
        ro.observe(wrapper);
      }

      // Smooth out cursor jitters
      var existingTransition = img.style.transition || '';
      img.style.transition = (existingTransition ? existingTransition + ', ' : '') + 'object-position 0.15s ease-out';

      var onMove = function(e) {
        var rect = wrapper.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        var nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        var ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
        var px = 50 - nx * panX;
        var py = 50 - ny * panY;
        img.style.setProperty('object-position', px.toFixed(2) + '% ' + py.toFixed(2) + '%', 'important');
      };
      var onLeave = function() {
        img.style.setProperty('object-position', '50% 50%', 'important');
      };

      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);

      wrapper.dataset.zargesParallaxReady = '1';
    });
  }

  // Run video + parallax + mobile carousel setup on initial load + whenever
  // the USF grid re-renders. Re-run on a delay to catch lazy elements.
  function scheduleVideoSetup() {
    setupVideoHover();
    setupThemeSecondaryParallax();
    setupMobileCarousel();
    setTimeout(function() { setupVideoHover(); setupThemeSecondaryParallax(); setupMobileCarousel(); }, 500);
    setTimeout(function() { setupVideoHover(); setupThemeSecondaryParallax(); setupMobileCarousel(); }, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleVideoSetup);
  } else {
    scheduleVideoSetup();
  }

  // ---- Inject overlay + secondary image on all cards in the USF grid -----
  function enhanceCards() {
    var cards = document.querySelectorAll('#usf_container [data-usf-pid]');
    if (!cards.length) return;

    cards.forEach(function(card) {
      var pid = card.getAttribute('data-usf-pid');
      var product = findProductById(pid);
      if (!product) return;

      // ---- Spec overlay (skip if already added) ----
      if (!card.querySelector('.zarges-spec-overlay')) {
        var specs = buildSpecs(product);
        if (specs.hasAny) {
          var mediaWrap = card.querySelector('.product-card__media');
          if (mediaWrap) {
            var computed = window.getComputedStyle(mediaWrap);
            if (computed.position === 'static') {
              mediaWrap.style.position = 'relative';
            }
            mediaWrap.style.overflow = 'hidden';
            mediaWrap.insertAdjacentHTML('beforeend', buildOverlayHtml(product, specs));
          }
        }
      }

      // ---- Secondary image on hover ----
      try {
        setupSecondaryImage(card, product);
      } catch (e) {
        // Don't let secondary-image errors break the overlay
        console.warn('[Zarges] Secondary image setup failed for', pid, e);
      }
    });

    // ---- Wire up videos + theme-secondary parallax + mobile carousel ----
    setupVideoHover();
    setupThemeSecondaryParallax();
    setupMobileCarousel();
  }

  // ---- Run the enhancement, debounced ------------------------------------
  var debounceTimer = null;
  function scheduleEnhance() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(enhanceCards, 50);
  }

  // ---- Wire up to USF events ---------------------------------------------
  waitForUsf(function() {
    scheduleEnhance();

    window.usf.event.add(['sr_updated', 'sr_viewChanged', 'rerender'], scheduleEnhance);

    var container = document.getElementById('usf_container');
    if (container && window.MutationObserver) {
      var observer = new MutationObserver(function(mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            scheduleEnhance();
            return;
          }
        }
      });
      observer.observe(container, { childList: true, subtree: true });
    }
  });
})();
